import { useState, useEffect, useMemo, useCallback, type CSSProperties } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

export interface TourStep {
  /** CSS selector for the element to highlight */
  target: string;
  /** Title shown in the callout */
  title: string;
  /** Description shown in the callout */
  description: string;
  /** Preferred placement of the callout relative to the target */
  placement?: "top" | "bottom" | "left" | "right";
  /** Route to navigate to before showing this step */
  route?: string;
  /** Callback executed before showing this step (e.g. to switch tabs) */
  beforeShow?: () => void;
}

interface ProductTourProps {
  steps: TourStep[];
  onComplete: () => void;
}

type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const MASK_OPACITY_CLASS = "bg-foreground/60";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export default function ProductTour({ steps, onComplete }: ProductTourProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [renderStep, setRenderStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [visible, setVisible] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const step = steps[currentStep];
  const isLast = renderStep === steps.length - 1;

  const measureTarget = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return false;
    }

    const element = document.querySelector(step.target);
    if (!element) {
      setTargetRect(null);
      return false;
    }

    setTargetRect(element.getBoundingClientRect());
    return true;
  }, [step?.target]);

  useEffect(() => {
    if (!step) return;

    setVisible(false);
    let cancelled = false;
    let retryTimer: number | undefined;

    const needsRouteChange = Boolean(step.route && location.pathname !== step.route);
    if (needsRouteChange) {
      navigate(step.route as string);
    }

    const attemptMeasure = (attempt = 0) => {
      if (cancelled) return;

      const found = measureTarget();
      if (found) {
        setRenderStep(currentStep);
        setVisible(true);
        return;
      }

      if (attempt >= 10) {
        setVisible(true);
        return;
      }

      retryTimer = window.setTimeout(() => attemptMeasure(attempt + 1), 120);
    };

    const kickoffTimer = window.setTimeout(() => {
      step.beforeShow?.();
      window.setTimeout(() => attemptMeasure(), needsRouteChange ? 280 : 80);
    }, needsRouteChange ? 420 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(kickoffTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [currentStep, location.pathname, measureTarget, navigate, step]);

  useEffect(() => {
    if (!visible) return;

    const remeasure = () => measureTarget();
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);

    return () => {
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [measureTarget, visible]);

  const spotlightRect = useMemo<SpotlightRect | null>(() => {
    if (!targetRect) return null;

    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const left = clamp(targetRect.left - padding, 0, viewportWidth);
    const top = clamp(targetRect.top - padding, 0, viewportHeight);
    const right = clamp(targetRect.right + padding, 0, viewportWidth);
    const bottom = clamp(targetRect.bottom + padding, 0, viewportHeight);

    return {
      left,
      top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
  }, [targetRect]);

  const maskSegments = useMemo<CSSProperties[]>(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (!spotlightRect) {
      return [{ top: 0, left: 0, width: viewportWidth, height: viewportHeight }];
    }

    const { top, left, width, height } = spotlightRect;

    return [
      { top: 0, left: 0, width: viewportWidth, height: top },
      { top, left: 0, width: left, height },
      { top, left: left + width, width: Math.max(0, viewportWidth - (left + width)), height },
      { top: top + height, left: 0, width: viewportWidth, height: Math.max(0, viewportHeight - (top + height)) },
    ];
  }, [spotlightRect]);

  const calloutLayout = useMemo(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.min(360, viewportWidth - 32);
    const padding = 16;
    const gap = 24;

    const noTarget = {
      style: {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: maxWidth,
      } as CSSProperties,
      arrowStyle: null as CSSProperties | null,
    };

    if (!spotlightRect || !step) return noTarget;

    const placement = step.placement || "right";
    const centerY = spotlightRect.top + spotlightRect.height / 2;
    const centerX = spotlightRect.left + spotlightRect.width / 2;

    // Helper to compute arrow position
    const makeArrow = (top: number, left: number): CSSProperties => ({
      position: "absolute",
      top: top - 6,
      left: left - 6,
    });

    if (placement === "right") {
      const left = clamp(spotlightRect.left + spotlightRect.width + gap, padding, viewportWidth - maxWidth - padding);
      const top = clamp(centerY, 110, viewportHeight - 110);
      return {
        style: { top, left, transform: "translateY(-50%)", width: maxWidth } as CSSProperties,
        arrowStyle: makeArrow(top, spotlightRect.left + spotlightRect.width + gap / 2),
      };
    }

    if (placement === "left") {
      const left = clamp(spotlightRect.left - gap - maxWidth, padding, viewportWidth - maxWidth - padding);
      const top = clamp(centerY, 110, viewportHeight - 110);
      return {
        style: { top, left, transform: "translateY(-50%)", width: maxWidth } as CSSProperties,
        arrowStyle: makeArrow(top, spotlightRect.left - gap / 2),
      };
    }

    if (placement === "bottom") {
      const top = clamp(spotlightRect.top + spotlightRect.height + gap, 16, viewportHeight - 16);
      const left = clamp(centerX - maxWidth / 2, padding, viewportWidth - maxWidth - padding);
      return {
        style: { top, left, width: maxWidth } as CSSProperties,
        arrowStyle: makeArrow(spotlightRect.top + spotlightRect.height + gap / 2, centerX),
      };
    }

    if (placement === "top") {
      const top = clamp(spotlightRect.top + gap, padding, viewportHeight - 200);
      const left = clamp(centerX - maxWidth / 2, padding, viewportWidth - maxWidth - padding);
      return {
        style: { top, left, width: maxWidth } as CSSProperties,
        arrowStyle: null,
      };
    }

    // Default: right
    const left = clamp(spotlightRect.left + spotlightRect.width + gap, padding, viewportWidth - maxWidth - padding);
    const top = clamp(centerY, 110, viewportHeight - 110);
    return {
      style: { top, left, transform: "translateY(-50%)", width: maxWidth } as CSSProperties,
      arrowStyle: makeArrow(top, spotlightRect.left + spotlightRect.width + gap / 2),
    };
  }, [spotlightRect, step]);

  const handleNext = () => {
    if (isLast) {
      onComplete();
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-[300]">
      {/* Gray overlay segments */}
      {maskSegments.map((segment, index) => (
        <div
          key={`mask-${index}`}
          className="absolute bg-foreground/50"
          style={segment}
        />
      ))}

      {/* Dashed border highlight around target */}
      {spotlightRect && (
        <div
          className="absolute pointer-events-none rounded-lg transition-all duration-300"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            border: '2px dashed hsl(var(--primary))',
          }}
        />
      )}

      {/* Connector arrow (small diamond/triangle pointing from callout to spotlight) */}
      {visible && spotlightRect && calloutLayout.arrowStyle && (
        <div
          className="absolute z-[305]"
          style={calloutLayout.arrowStyle}
        >
          <div
            className="h-3 w-3 rotate-45 bg-foreground"
          />
        </div>
      )}

      {/* Dark callout card */}
      {visible && (
        <div
          className="absolute z-[310] rounded-xl bg-foreground p-5 shadow-xl"
          style={{ ...calloutLayout.style, maxWidth: calloutLayout.style.width }}
        >
          <button
            onClick={onComplete}
            aria-label={t("wizards.tourClose")}
            className="absolute right-3 top-3 rounded-md p-1 text-background/60 transition hover:bg-background/10 hover:text-background"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <h3 className="pr-6 text-sm font-semibold text-background">{step.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-background/70">{step.description}</p>

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-background/60">
              {t("wizards.tourStepOf", { current: renderStep + 1, total: steps.length })}
            </span>
            <button
              onClick={handleNext}
              className="rounded-md bg-background px-4 py-1.5 text-xs font-medium text-foreground transition hover:bg-background/90"
            >
              {isLast ? t("wizards.tourFinish") : t("wizards.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
